import { createContext, useCallback, useContext, useRef, useSyncExternalStore, type ReactNode, type SetStateAction } from 'react';
import type { MatrixSide } from '../lib/database';

export type NewContentionDraft = {
  scope: string; pleadingId: string; claimId: string; side: MatrixSide;
  text: string; originId: string; status: 'draft' | 'submitting' | 'uncertain' | 'saved'; message: string | null;
};

// Lives only inside the authenticated route tree; no document text is stored on
// disk. The provider is keyed to the signed-in identity and destroyed at signout.
function createStore() {
  const drafts = new Map<string, NewContentionDraft>();
  const revisions = new Map<string, { matrix: number; origins: number }>();
  const initialRevision = Object.freeze({ matrix: 0, origins: 0 });
  const listeners = new Set<() => void>();
  return {
    get: (caseId: string) => drafts.get(caseId) ?? null,
    getRevision: (caseId: string) => revisions.get(caseId) ?? initialRevision,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    set: (caseId: string, action: SetStateAction<NewContentionDraft | null>) => {
      const current = drafts.get(caseId) ?? null;
      const next = typeof action === 'function' ? action(current) : action;
      if (next === current) return;
      if (next) drafts.set(caseId, next); else drafts.delete(caseId);
      listeners.forEach((listener) => listener());
    },
    invalidate: (caseId: string, kind: 'matrix' | 'origins') => {
      const current = revisions.get(caseId) ?? initialRevision;
      revisions.set(caseId, { ...current, [kind]: current[kind] + 1 });
      listeners.forEach((listener) => listener());
    },
  };
}
type DraftStore = ReturnType<typeof createStore>;
const MatrixCreateDraftContext = createContext<DraftStore | null>(null);

export function MatrixCreateDraftProvider({ children }: { children: ReactNode }) {
  const store = useRef<DraftStore | null>(null);
  if (!store.current) store.current = createStore();
  return <MatrixCreateDraftContext.Provider value={store.current}>{children}</MatrixCreateDraftContext.Provider>;
}

export function useMatrixCreateDraft(caseId: string) {
  const provided = useContext(MatrixCreateDraftContext);
  // Standalone screen tests can render without the app's authenticated route.
  const local = useRef<DraftStore | null>(null);
  if (!local.current) local.current = createStore();
  const store = provided ?? local.current;
  const get = useCallback(() => store.get(caseId), [store, caseId]);
  const draft = useSyncExternalStore(store.subscribe, get, get);
  const getRevision = useCallback(() => store.getRevision(caseId), [store, caseId]);
  const revisions = useSyncExternalStore(store.subscribe, getRevision, getRevision);
  const setDraft = useCallback((action: SetStateAction<NewContentionDraft | null>) => store.set(caseId, action), [store, caseId]);
  const invalidate = useCallback((kind: 'matrix' | 'origins') => store.invalidate(caseId, kind), [store, caseId]);
  return { draft, setDraft, getDraft: get, revisions, invalidate };
}
